/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Stack, Table, Spinner } from '@chakra-ui/react';
import {
  File,
  Plus,
  UploadSimple,
  X,
  CheckCircle,
  XCircle,
  ArrowClockwise,
  Trash
} from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchAPI } from '@/api/fetch';
import {
  apiCreateCandidate,
  apiCreateCandidateFromVeteransResume,
  apiGetAgencyCustomFeatures,
  apiGetExtractorProviders
} from '@/api/services';
import { Candidate } from '@/types/Candidate';
import { AnalysisProvider } from '@/types/AnalysisProvider';
import { AgencyCustomFeature } from '@/types/AgencyCustomFeature';
import {
  AgencyFeature,
  LlmModelProvider,
  PromptKey,
  getLlmModelProviderSupportFileTypes
} from '@/types/enums';

import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { getFileSize } from '@/utils';
import { MAX_RESUME_FILE_SIZE } from '@/config/constants';

// Constants
const MAX_CONCURRENT_UPLOADS = 3;
const SUPPORTED_FILE_TYPES = ['.pdf', '.txt'];
const FILE_SIZE_LIMIT = MAX_RESUME_FILE_SIZE;
const MAX_FILES = 20;

// Types
type FileStatus = 'pending' | 'submitting' | 'success' | 'error';

interface FileWithStatus {
  file: File;
  status: FileStatus;
  error?: string;
}

interface Provider {
  analysis_id: string | null;
  analysis_name: PromptKey;
  provider: LlmModelProvider | null;
  loading: boolean;
  error: string | null;
}

interface AddNewCandidateProps {
  onAddCandidate: () => void;
  filled?: boolean;
  disabled?: boolean;
}

const getStatusColor = (status: FileStatus): string => {
  const statusColors = {
    success: '#38A169',
    error: '#E53E3E',
    submitting: '#3182CE',
    pending: '#718096'
  };
  return statusColors[status];
};

const validateFile = (file: File): string | null => {
  const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

  if (!SUPPORTED_FILE_TYPES.includes(fileExtension)) {
    return `File type ${fileExtension} is not supported. Please upload ${SUPPORTED_FILE_TYPES.join(
      ', '
    )} files`;
  }

  if (file.size > FILE_SIZE_LIMIT) {
    return `File size exceeds ${getFileSize(FILE_SIZE_LIMIT)}`;
  }

  return null;
};

type UploadMode = 'standard' | 'veterans';

export default function AddNewCandidate({
  onAddCandidate,
  filled = false,
  disabled = false
}: AddNewCandidateProps) {
  const [open, setOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState<UploadMode>('standard');
  const [hasVeteransResumeFeature, setHasVeteransResumeFeature] = useState(false);
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [provider, setProvider] = useState<Provider>({
    analysis_id: null,
    analysis_name: PromptKey.ResumeExtractor,
    provider: null,
    loading: true,
    error: null
  });

  const defaultProvider: Provider = {
    analysis_id: null,
    analysis_name: PromptKey.ResumeExtractor,
    provider: null,
    loading: false,
    error: null
  };

  const fetchProviders = async () => {
    setProvider({ ...defaultProvider, loading: true, error: null });

    try {
      const response = await fetchAPI<AnalysisProvider[]>({
        request: apiGetExtractorProviders
      });

      if (response.success) {
        const providersData = response.data;
        const extractorProvider = providersData.find(
          (p) => p.analysis_name === PromptKey.ResumeExtractor
        );

        if (extractorProvider) {
          setProvider({
            ...extractorProvider,
            loading: false,
            error: null
          });
        } else {
          console.error('No CV extractor provider configured.');
          setProvider({
            ...defaultProvider,
            error:
              'No CV extractor provider configured. Please contact support to enable file upload.'
          });
        }
      } else {
        console.error('Error fetching analysis providers:', response.message);
        setProvider({
          ...defaultProvider,
          error: 'Failed to load file upload options. Please try again later.'
        });
      }
    } catch (error) {
      console.error('Unexpected error fetching analysis providers:', error);
      setProvider({
        ...defaultProvider,
        error: 'Failed to load file upload options. Please try again later.'
      });
    }
  };

  const retryProviderFetch = useCallback(() => {
    fetchProviders();
  }, []);

  // Computed values
  const pendingFiles = useMemo(() => files.filter((f) => f.status === 'pending'), [files]);
  const submittingFiles = useMemo(() => files.filter((f) => f.status === 'submitting'), [files]);
  const errorFiles = useMemo(() => files.filter((f) => f.status === 'error'), [files]);
  const successFiles = useMemo(() => files.filter((f) => f.status === 'success'), [files]);

  const hasPendingOrSubmitting = pendingFiles.length > 0 || submittingFiles.length > 0;
  const canUpload = files.length > 0 && !hasPendingOrSubmitting;
  const canAddMore = files.length < MAX_FILES;

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      fetchProviders();
      setFiles([]);
      setFileError(null);
      setUploadMode('standard');
      fetchAPI<AgencyCustomFeature[]>({ request: apiGetAgencyCustomFeatures }).then((res) => {
        if (res.success) {
          setHasVeteransResumeFeature(
            res.data.some((f) => f.feature_key === AgencyFeature.VeteransResume && f.is_enabled)
          );
        }
      });
    }
  }, [open]);

  // File submission logic
  const submitFile = useCallback(
    async (fileWithStatus: FileWithStatus, index: number) => {
      setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, status: 'submitting' } : f)));

      try {
        const apiCall =
          uploadMode === 'veterans'
            ? () => apiCreateCandidateFromVeteransResume({ payload: { file: fileWithStatus.file } })
            : () => apiCreateCandidate({ payload: { resume: fileWithStatus.file } });

        const candidateResponse = await fetchAPI<Candidate>({ request: apiCall });

        if (candidateResponse.success) {
          setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, status: 'success' } : f)));
        } else {
          setFiles((prev) =>
            prev.map((f, i) =>
              i === index ? { ...f, status: 'error', error: candidateResponse.message } : f
            )
          );
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        setFiles((prev) =>
          prev.map((f, i) =>
            i === index ? { ...f, status: 'error', error: 'Failed to upload file' } : f
          )
        );
      }
    },
    [uploadMode]
  );

  // Batch upload processing
  const onSubmit = useCallback(async () => {
    if (files.length === 0) {
      setFileError('At least one file is required');
      return;
    }

    setIsSubmitting(true);
    setFileError(null);

    try {
      const pendingIndices = pendingFiles.map((file) =>
        files.findIndex((f) => f.file === file.file)
      );

      // Process files in batches to limit concurrent uploads
      for (let i = 0; i < pendingFiles.length; i += MAX_CONCURRENT_UPLOADS) {
        const batch = pendingFiles.slice(i, i + MAX_CONCURRENT_UPLOADS);
        const batchIndices = pendingIndices.slice(i, i + MAX_CONCURRENT_UPLOADS);
        await Promise.all(batch.map((file, j) => submitFile(file, batchIndices[j])));
      }
    } catch (error) {
      console.error('Error submitting files:', error);
      setFileError('An unexpected error occurred while uploading files. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [files, pendingFiles, submitFile]);

  // File handling
  const handleFileUpload = useCallback(
    (uploadedFiles: File[]) => {
      if (provider?.error || provider?.loading) {
        setFileError('File upload is not available. Please wait or try again later.');
        return;
      }

      const errors: string[] = [];

      if (files.length + uploadedFiles.length > MAX_FILES) {
        setFileError(`Maximum ${MAX_FILES} files allowed`);
        return;
      }

      const existingFileNames = new Set(files.map((f) => f.file.name));
      const uniqueNewFiles = uploadedFiles.filter((file) => !existingFileNames.has(file.name));

      if (uniqueNewFiles.length < uploadedFiles.length) {
        const duplicateFiles = uploadedFiles.filter((file) => existingFileNames.has(file.name));
        errors.push(`Skipped duplicate files: ${duplicateFiles.map((f) => f.name).join(', ')}`);
      }

      uniqueNewFiles.forEach((file) => {
        const error = validateFile(file);
        if (error) errors.push(`${file.name}: ${error}`);
      });

      if (errors.length > 0) {
        setFileError(errors.join('\n'));
        return;
      }

      setFileError(null);
      setFiles((prev) => [
        ...prev,
        ...uniqueNewFiles.map((file) => ({
          file,
          status: 'pending' as FileStatus
        }))
      ]);
    },
    [files, provider]
  );

  const onUploadFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const uploadedFiles = Array.from(e.target.files || []);
      handleFileUpload(uploadedFiles);
      // Reset input value to allow re-uploading the same file
      e.target.value = '';
    },
    [handleFileUpload]
  );

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFileError(null);
  }, []);

  const handleRetryFile = useCallback(
    (fileWithStatus: FileWithStatus, index: number) => {
      submitFile(fileWithStatus, index);
    },
    [submitFile]
  );

  // Dialog handling
  const handleDialogClose = useCallback(() => {
    setFiles([]);
    setFileError(null);
    if (successFiles.length > 0) {
      onAddCandidate();
    }
  }, [successFiles.length, onAddCandidate]);

  const handleOpenChange = useCallback((e: { open: boolean }) => {
    setOpen(e.open);
  }, []);

  // Render helpers
  const renderFileStatus = (status: FileStatus) => {
    switch (status) {
      case 'success':
        return <CheckCircle size={20} color={getStatusColor(status)} />;
      case 'error':
        return <XCircle size={20} color={getStatusColor(status)} />;
      case 'submitting':
        return <Box>Uploading...</Box>;
      case 'pending':
        return <Box>Pending</Box>;
      default:
        return null;
    }
  };

  const renderFileActions = (fileWithStatus: FileWithStatus, index: number) => (
    <Box display="flex" alignItems="center" justifyContent="center" gap="2">
      {fileWithStatus.status === 'error' && (
        <Button
          variant="ghost"
          size="xs"
          borderRadius="full"
          p="0"
          m="0"
          onClick={() => handleRetryFile(fileWithStatus, index)}
          color="primary.900"
          title="Retry upload"
        >
          <ArrowClockwise size={16} />
        </Button>
      )}
      {(fileWithStatus.status === 'pending' || fileWithStatus.status === 'error') && (
        <Button
          variant="ghost"
          size="xs"
          borderRadius="full"
          p="0"
          m="0"
          onClick={() => handleRemoveFile(index)}
          color="neutrals.900"
          title="Remove file"
        >
          <Trash size={16} />
        </Button>
      )}
    </Box>
  );

  return (
    <DialogRoot
      size="xl"
      open={open}
      onOpenChange={handleOpenChange}
      closeOnInteractOutside={false}
      placement="center"
      motionPreset="slide-in-bottom"
      onExitComplete={handleDialogClose}
    >
      <DialogTrigger asChild>
        {filled ? (
          <Button
            w="240px"
            h="48px"
            borderRadius="8px"
            bg="primary.900"
            fontSize="16px"
            fontWeight="regular"
            disabled={disabled}
          >
            <Plus size={24} />
            Add New Candidate
          </Button>
        ) : (
          <Button
            borderColor="primary.900"
            background="background.secondary"
            color="primary.900"
            borderRadius="8px"
            ml="16px"
            fontSize="16px"
            disabled={disabled}
          >
            <Plus size={24} />
            Add New Candidate
          </Button>
        )}
      </DialogTrigger>

      <DialogContent borderRadius="12px">
        <DialogHeader>
          <DialogTitle>Add New Candidates</DialogTitle>
        </DialogHeader>

        <DialogBody>
          {provider.loading ? (
            <Box
              w="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              py="120px"
            >
              <Stack align="center">
                <Spinner
                  w="60px"
                  h="60px"
                  borderWidth="6px"
                  color="primary.900"
                  css={{ '--spinner-track-color': 'colors.gray.200' }}
                  mb="16px"
                />
                <Box fontSize="20px" fontWeight="bold" color="neutrals.900">
                  Loading...
                </Box>
                <Box fontSize="14px" color="neutrals.600">
                  Configuring resume upload settings
                </Box>
              </Stack>
            </Box>
          ) : (
            <Stack gap="4">
              <Box as="hr" borderColor="neutrals.100" my="4px" />

              {hasVeteransResumeFeature && (
                <Box display="flex" gap="2">
                  <Button
                    size="sm"
                    borderRadius="8px"
                    fontSize="14px"
                    variant={uploadMode === 'standard' ? 'solid' : 'outline'}
                    background={uploadMode === 'standard' ? 'primary.900' : 'background.secondary'}
                    color={uploadMode === 'standard' ? 'neutrals.0' : 'primary.900'}
                    borderColor="primary.900"
                    onClick={() => {
                      setUploadMode('standard');
                      setFiles([]);
                      setFileError(null);
                    }}
                    disabled={isSubmitting}
                  >
                    Standard Resume
                  </Button>
                  <Button
                    size="sm"
                    borderRadius="8px"
                    fontSize="14px"
                    variant={uploadMode === 'veterans' ? 'solid' : 'outline'}
                    background={uploadMode === 'veterans' ? 'primary.900' : 'background.secondary'}
                    color={uploadMode === 'veterans' ? 'neutrals.0' : 'primary.900'}
                    borderColor="primary.900"
                    onClick={() => {
                      setUploadMode('veterans');
                      setFiles([]);
                      setFileError(null);
                    }}
                    disabled={isSubmitting}
                  >
                    Veterans Resume
                  </Button>
                </Box>
              )}

              <Box>
                <Box color="neutrals.800" fontSize="18px" mb="4px">
                  <Box as="span" color="error.500">
                    *
                  </Box>
                  Upload up to {MAX_FILES} resumes or CVs, and we'll automatically extract candidate
                  information.
                </Box>
                <Box color="neutrals.500" fontSize="16px">
                  {(() => {
                    const types = provider?.provider
                      ? getLlmModelProviderSupportFileTypes(provider.provider)
                      : ['.pdf'];
                    const label = types.length === 1 ? 'format' : 'formats';
                    return (
                      <>
                        Supported {label}: {types.join(', ')} ({getFileSize(FILE_SIZE_LIMIT)} max
                        per file)
                      </>
                    );
                  })()}
                </Box>
                {provider?.error && (
                  <Box
                    fontSize="16px"
                    color="error.700"
                    mt="8px"
                    p="16px"
                    bg="error.50"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor="error.200"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center">
                      <Box mr="8px">⚠️</Box>
                      <Box>{provider?.error}</Box>
                    </Box>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      onClick={retryProviderFetch}
                      ml="16px"
                      flexShrink={0}
                      color="neutrals.700"
                      fontSize="16px"
                      borderColor="neutrals.500"
                      borderRadius="8px"
                    >
                      Retry
                    </Button>
                  </Box>
                )}
              </Box>

              {files.length > 0 ? (
                <Stack gap="4">
                  <Box width="100%" overflowX="auto">
                    <Table.Root>
                      <Table.Header>
                        <Table.Row bg="neutrals.50" h="48px">
                          <Table.ColumnHeader
                            fontSize="14px"
                            fontWeight="bold"
                            borderColor="neutrals.300"
                            width="50%"
                          >
                            File Name
                          </Table.ColumnHeader>
                          <Table.ColumnHeader
                            fontSize="14px"
                            fontWeight="bold"
                            borderColor="neutrals.300"
                            width="15%"
                          >
                            <Box display="flex" alignItems="center" justifyContent="center">
                              Size
                            </Box>
                          </Table.ColumnHeader>
                          <Table.ColumnHeader
                            fontSize="14px"
                            fontWeight="bold"
                            borderColor="neutrals.300"
                            width="20%"
                          >
                            <Box display="flex" alignItems="center" justifyContent="center">
                              Status
                            </Box>
                          </Table.ColumnHeader>
                          <Table.ColumnHeader
                            fontSize="14px"
                            fontWeight="bold"
                            borderColor="neutrals.300"
                            width="15%"
                          >
                            <Box display="flex" alignItems="center" justifyContent="center">
                              Action
                            </Box>
                          </Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {files.map((fileWithStatus, index) => (
                          <Table.Row key={`${fileWithStatus.file.name}-${index}`}>
                            <Table.Cell width="50%">
                              <Box display="flex" alignItems="center">
                                <File size={24} color="#4e76ff" />
                                <Box
                                  ml="12px"
                                  maxW="300px"
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  whiteSpace="nowrap"
                                  title={fileWithStatus.file.name}
                                >
                                  {fileWithStatus.file.name}
                                </Box>
                              </Box>
                            </Table.Cell>
                            <Table.Cell width="15%">
                              <Box display="flex" alignItems="center" justifyContent="center">
                                {getFileSize(fileWithStatus.file.size)}
                              </Box>
                            </Table.Cell>
                            <Table.Cell width="20%">
                              <Box display="flex" alignItems="center" justifyContent="center">
                                {renderFileStatus(fileWithStatus.status)}
                              </Box>
                            </Table.Cell>
                            <Table.Cell width="15%">
                              {renderFileActions(fileWithStatus, index)}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>

                  <Box>
                    <input
                      id="additionalFiles"
                      type="file"
                      accept={
                        provider?.provider
                          ? getLlmModelProviderSupportFileTypes(provider.provider).join(',')
                          : '.pdf'
                      }
                      multiple
                      style={{ display: 'none' }}
                      onChange={onUploadFile}
                      disabled={!!provider?.error || provider?.loading}
                    />
                    <Button
                      variant="outline"
                      borderRadius="8px"
                      border="1px solid"
                      borderColor="neutrals.300"
                      color="neutrals.700"
                      fontSize="14px"
                      onClick={() => document.getElementById('additionalFiles')?.click()}
                      disabled={!canAddMore || !!provider?.error || provider?.loading}
                    >
                      <Plus size={26} />
                      Add More Files ({files.length}/{MAX_FILES})
                    </Button>
                  </Box>

                  {fileError && (
                    <Box
                      color="error.700"
                      fontSize="14px"
                      whiteSpace="pre-line"
                      p="12px"
                      bg="error.50"
                      borderRadius="8px"
                    >
                      {fileError}
                    </Box>
                  )}
                </Stack>
              ) : (
                <Stack>
                  <Box
                    w="100%"
                    h="200px"
                    border="1px dashed"
                    borderColor={
                      provider?.error ? 'error.700' : fileError ? 'error.700' : 'neutrals.300'
                    }
                    borderRadius="12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor={provider?.error || provider?.loading ? 'not-allowed' : 'pointer'}
                    bg={provider?.error ? 'error.25' : 'neutrals.50'}
                    position="relative"
                    _hover={{
                      bg: provider?.error || provider?.loading ? undefined : 'neutrals.100'
                    }}
                    transition="background-color 0.2s"
                    opacity={provider?.error || provider?.loading ? 0.6 : 1}
                  >
                    <input
                      id="resumeFile"
                      type="file"
                      accept={
                        provider?.provider
                          ? getLlmModelProviderSupportFileTypes(provider.provider).join(',')
                          : '.pdf'
                      }
                      multiple
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: provider?.error || provider?.loading ? 'not-allowed' : 'pointer'
                      }}
                      onChange={onUploadFile}
                      disabled={!!provider?.error || provider?.loading}
                    />
                    <Box display="flex" flexDirection="column" alignItems="center" fontSize="14px">
                      <UploadSimple size={60} color="#404040" />
                      <Box textAlign="center" color="neutrals.900">
                        Drop files here or{' '}
                        <Box as="span" color="primary.900" fontWeight="medium">
                          click to upload
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {fileError && (
                    <Box
                      fontSize="14px"
                      color="error.700"
                      whiteSpace="pre-line"
                      p="12px"
                      bg="error.50"
                      borderRadius="8px"
                    >
                      {fileError}
                    </Box>
                  )}
                </Stack>
              )}
              {!provider.loading && errorFiles.length > 0 && (
                <Box
                  color="error.500"
                  fontSize="16px"
                  mt="16px"
                  p="12px"
                  bg="error.50"
                  borderRadius="8px"
                >
                  Some files failed to upload. You can retry failed uploads individually.
                </Box>
              )}
            </Stack>
          )}
        </DialogBody>

        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button
              w="120px"
              borderColor="primary.900"
              background="background.secondary"
              color="primary.900"
              borderRadius="8px"
              fontSize="16px"
              disabled={isSubmitting || provider?.loading}
            >
              Close
            </Button>
          </DialogActionTrigger>

          {!canUpload ? (
            <Button
              w="120px"
              background="primary.900"
              color="neutrals.0"
              borderRadius="8px"
              fontSize="16px"
              onClick={onSubmit}
              loading={isSubmitting}
              disabled={files.length === 0 || isSubmitting || !!provider?.error}
            >
              {isSubmitting ? 'Uploading...' : 'Upload'}
            </Button>
          ) : (
            <DialogActionTrigger asChild>
              <Button
                w="120px"
                background="primary.900"
                color="neutrals.0"
                borderRadius="8px"
                fontSize="16px"
              >
                Done
              </Button>
            </DialogActionTrigger>
          )}
        </DialogFooter>

        <DialogCloseTrigger m="12px" disabled={isSubmitting || provider?.loading}>
          <X size={32} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
